
const stages = [
  {
    num: "01",
    label: "Research",
    title: "Research",
    desc: "Real-time news, on-chain analytics, and sentiment — surfaced and synthesized into opportunities worth a look.",
    bullets: ["Live news streams", "On-chain flows & liquidations", "Sentiment aggregation"],
    accent: "cyan",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" />
      </svg>
    ),
  },
  {
    num: "02",
    label: "Setups",
    title: "Setups",
    desc: (
      <>
        High-conviction recommendations with the{" "}
        <span className="text-purple-400 font-semibold">reasoning behind them</span> — direction, R:R, leverage, and why now.
      </>
    ),
    bullets: ["Conviction & confidence scoring", "Reasons & counter-risks", "Pre-set R:R and sizing"],
    accent: "purple",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 18l5-6 4 4 8-10" />
        <path d="M14 6h6v6" />
      </svg>
    ),
  },
  {
    num: "03",
    label: "Execute",
    title: "Execute",
    desc: (
      <>
        One-click orders with{" "}
        <span className="text-green-400 font-semibold">stop-loss, take-profit, and trailing</span> built in. Your approval, every time.
      </>
    ),
    bullets: ["One-click approval", "SL / TP / trailing baked in", "Live P&L & exit monitoring"],
    accent: "green",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12l5 5L20 6" />
      </svg>
    ),
  },
];

const ACCENT = {
  cyan: {
    glow: "from-cyan-500/20 to-transparent",
    border: "border-cyan-500/20",
    num: "text-cyan-400",
    dot: "text-cyan-400 border-white/10",
    bullet: "bg-cyan-400 shadow-[0_0_8px_theme(colors.cyan.400)]",
  },
  purple: {
    glow: "from-purple-500/20 to-transparent",
    border: "border-purple-500/20",
    num: "text-purple-400",
    dot: "text-purple-400 border-white/10",
    bullet: "bg-purple-400 shadow-[0_0_8px_theme(colors.purple.400)]",
  },
  green: {
    glow: "from-green-400/20 to-transparent",
    border: "border-green-400/20",
    num: "text-green-400",
    dot: "text-green-400 border-white/10",
    bullet: "bg-green-400 shadow-[0_0_8px_theme(colors.green.400)]",
  },
};

function Arrow() {
  return (
    <div className="hidden lg:flex items-center justify-center text-white/20" aria-hidden="true">
      <svg viewBox="0 0 60 34" width="48" height="28" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 17h48" />
        <path d="M44 9l10 8-10 8" />
      </svg>
    </div>
  );
}

function StageCard({ stage }) {
  const a = ACCENT[stage.accent];
  return (
    <div className={`relative flex flex-col rounded-2xl border border-white/[0.12] bg-gradient-to-b from-white/[0.035] to-white/[0.01] p-5 sm:p-7 overflow-hidden`}>
      {/* Top glow */}
      <div className={`absolute inset-0 bg-gradient-radial ${a.glow} opacity-90 pointer-events-none`}
        style={{ background: `radial-gradient(280px 140px at 50% 0%, var(--tw-gradient-from), transparent 70%)` }}
      />

      {/* Stage header */}
      <div className="relative z-[1] flex items-center justify-between mb-4">
        <span className={`font-mono text-[11px] sm:text-xs tracking-[0.22em] uppercase ${a.num}`}>
          Stage&nbsp;{stage.num} / {stage.label}
        </span>
        <span className={`w-10 h-10 rounded-xl border bg-white/[0.03] flex items-center justify-center ${a.dot}`}>
          {stage.icon}
        </span>
      </div>

      {/* Title */}
      <h3 className="relative z-[1] text-2xl sm:text-3xl lg:text-[2.5rem] font-bold tracking-tight text-white leading-none mb-3">
        {stage.title}
      </h3>

      {/* Description */}
      <p className="relative z-[1] text-sm sm:text-base text-gray-400 leading-relaxed mb-4">
        {stage.desc}
      </p>

      {/* Bullets */}
      <div className="relative z-[1] mt-auto flex flex-col gap-2 pt-4 border-t border-dashed border-white/[0.08]">
        {stage.bullets.map((b) => (
          <div key={b} className="flex items-center gap-2.5">
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${a.bullet}`} />
            <span className="font-mono text-[11px] sm:text-xs tracking-[0.1em] uppercase text-gray-400">{b}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AgenticWorkflow() {
  return (
    <section aria-labelledby="workflow-heading" className="relative py-10 md:py-16 bg-slate-900/60 w-full overflow-hidden">
      {/* Subtle grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-6 sm:px-10 lg:px-14">
        {/* Header */}
        <div className="mb-8 sm:mb-10 text-center">
          <p className="font-mono text-[11px] sm:text-xs tracking-[0.28em] uppercase text-cyan-400 mb-3">
            Meet Katbot · The agent that finds trades
          </p>
          <h2
            id="workflow-heading"
            className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-[1.05] tracking-tight text-white mb-3"
            style={{ textWrap: "balance" }}
          >
            AI that{" "}
            <span className="bg-gradient-to-r from-green-300 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
              finds
            </span>{" "}
            the trade,
            <br className="hidden sm:block" /> not just fires it.
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-gray-400 leading-relaxed max-w-3xl mx-auto">
            An agentic platform purpose-built for Hyperliquid perpetuals. One continuous loop — research, setup, execute — with you in the driver's seat.
          </p>
        </div>

        {/* Flow: 3 cards with arrows on lg+ */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_40px_1fr_40px_1fr] gap-4 lg:gap-0 items-stretch">
          <StageCard stage={stages[0]} />
          <Arrow />
          <StageCard stage={stages[1]} />
          <Arrow />
          <StageCard stage={stages[2]} />
        </div>

        {/* Closer bar */}
        <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5 px-4 sm:px-6 py-4 rounded-xl border border-white/[0.12] bg-gradient-to-r from-green-400/[0.06] to-cyan-400/[0.04]">
          <span className="font-mono text-[11px] sm:text-xs tracking-[0.22em] uppercase text-green-400 px-2.5 py-1 rounded-md border border-green-400/30 shrink-0">
            You stay in control
          </span>
          <p className="text-sm sm:text-base lg:text-lg text-white font-medium leading-snug">
            The AI handles the{" "}
            <span className="text-cyan-400 font-bold">research, discipline, and monitoring</span>{" "}
            you don't have time for.
          </p>
        </div>
      </div>
    </section>
  );
}
